'use client';

import { useWarehouseVariations } from '@util/hooks/useWarehouseVariations';
import {
  aggregateGroupFitment,
  filterFittingItems,
  findMatchingListing,
  isMXLockerDirect,
  isSelectedBikeComplete,
  isVehicleSpecificDirect,
  isVehicleSpecificDirectCandidate,
} from 'app/product/utils/vehicleSpecificDirect';
import { useBikeFilter } from 'context/BikeFilterContext';
import type { ProductDocument } from 'models/product';
import type { Bike } from 'models/shared';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

type WarehouseContextValue = {
  isActive: boolean;
  isLoading: boolean;
  mode: 'general' | 'specific';
  noFit: boolean;
  /** Card-browse only: show warehouse group title until a bike match / variant pick. */
  useGroupTitle: boolean;
  setUseGroupTitle: (value: boolean) => void;
  relatedItems: ProductDocument[];
  fittingItems: ProductDocument[];
  matchedListing: ProductDocument | null;
  aggregatedFitment: { bikes: Bike[]; allUniversal: boolean };
};

/** Survives card → PDP navigation when cards are outside WarehouseProvider. */
let pendingUseGroupTitle = false;

const setPendingUseGroupTitle = (value: boolean) => {
  pendingUseGroupTitle = value;
};

const INACTIVE: WarehouseContextValue = {
  isActive: false,
  isLoading: false,
  mode: 'specific',
  noFit: false,
  useGroupTitle: false,
  setUseGroupTitle: setPendingUseGroupTitle,
  relatedItems: [],
  fittingItems: [],
  matchedListing: null,
  aggregatedFitment: { bikes: [], allUniversal: false },
};

const WarehouseContext = createContext<WarehouseContextValue | undefined>(
  undefined
);

export function WarehouseProvider({
  product,
  landingProduct,
  warehouseGroupTitle,
  onSelectListing,
  children,
}: {
  product: ProductDocument;
  /** SSR landing listing (specific title) used when restoring after a bike clear. */
  landingProduct: ProductDocument;
  warehouseGroupTitle?: string;
  onSelectListing: (item: ProductDocument) => void;
  children: ReactNode;
}) {
  const { selectedBike } = useBikeFilter();
  const appliedListingRef = useRef<string | null>(null);
  const didApplyGroupTitleRef = useRef(false);

  const [useGroupTitle, setUseGroupTitleState] = useState(
    () => pendingUseGroupTitle && !!warehouseGroupTitle
  );

  // Consume the card-click flag once this PDP mounts.
  useEffect(() => {
    if (!pendingUseGroupTitle) return;
    pendingUseGroupTitle = false;
    setUseGroupTitleState(!!warehouseGroupTitle);
  }, [warehouseGroupTitle]);

  const setUseGroupTitle = useCallback(
    (value: boolean) => {
      pendingUseGroupTitle = value;
      if (!value) didApplyGroupTitleRef.current = false;
      setUseGroupTitleState(value && !!warehouseGroupTitle);
    },
    [warehouseGroupTitle]
  );

  const genericProduct = useMemo(
    () =>
      useGroupTitle && warehouseGroupTitle
        ? { ...landingProduct, title: warehouseGroupTitle }
        : landingProduct,
    [landingProduct, useGroupTitle, warehouseGroupTitle]
  );

  const isCandidate = isVehicleSpecificDirectCandidate(product);
  const hasWarehouseGroup =
    isMXLockerDirect(product) &&
    product.integration_info?.type === 'warehouse' &&
    !!product.group_id;
  const { relatedItems, isLoading } = useWarehouseVariations(
    hasWarehouseGroup ? product : null
  );

  const confirmed = useMemo(
    () =>
      isCandidate ? isVehicleSpecificDirect(product, relatedItems) : false,
    [isCandidate, product, relatedItems]
  );

  const isActive = isCandidate && (isLoading || confirmed);
  const hasVehicle = isSelectedBikeComplete(selectedBike);

  const matchedListing = useMemo(
    () =>
      isActive && hasVehicle
        ? findMatchingListing(relatedItems, selectedBike)
        : null,
    [isActive, hasVehicle, relatedItems, selectedBike]
  );

  const mode: 'general' | 'specific' =
    isActive && hasVehicle && matchedListing ? 'specific' : 'general';

  const aggregatedFitment = useMemo(
    () => aggregateGroupFitment(relatedItems),
    [relatedItems]
  );

  const isUniversal =
    !!product.fitting_bikes?.universal || aggregatedFitment.allUniversal;
  // A universal part fits every vehicle, so a missing vehicle match is not a no-fit.
  const noFit =
    isActive && hasVehicle && !matchedListing && !isLoading && !isUniversal;

  const fittingItems = useMemo(
    () => filterFittingItems(relatedItems, selectedBike),
    [relatedItems, selectedBike]
  );

  // Apply group title once on card-browse land. Do not re-run when the user
  // picks a color/size variant (product.id changes) or that would snap the
  // title back to the group title.
  useEffect(() => {
    if (!useGroupTitle || !warehouseGroupTitle) return;
    if (didApplyGroupTitleRef.current) return;
    if (matchedListing) return;
    didApplyGroupTitleRef.current = true;
    if (product.title === warehouseGroupTitle) return;
    onSelectListing(genericProduct);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useGroupTitle, warehouseGroupTitle, matchedListing?.id]);

  const vehicleKey = hasVehicle
    ? [
        selectedBike.type,
        selectedBike.make,
        selectedBike.model,
        selectedBike.year,
      ].join('|')
    : '';
  const genericProductId = genericProduct.id;
  const genericProductTitle = genericProduct.title;

  useEffect(() => {
    if (!isActive) return;
    // Wait for group variations when a vehicle is selected so we don't lock
    // onto the generic/group title before a match can be resolved.
    if (hasVehicle && isLoading) return;

    // Auto-select the vehicle's matched listing (or restore the generic
    // product when nothing matches) only once per vehicle change, so a manual
    // color/size swap by the user is not reverted back to the matched listing.
    const target = matchedListing ?? genericProduct;
    const targetKey = `${
      matchedListing ? matchedListing.id : 'generic'
    }:${vehicleKey}`;
    if (targetKey === appliedListingRef.current) return;
    appliedListingRef.current = targetKey;

    // Bike match always surfaces that specific listing. Title also matters when
    // the landing product carries a warehouse group_title but the matched
    // relatedItems copy is the same id with the listing's own title.
    if (target.id !== product.id || target.title !== product.title) {
      onSelectListing(target);
    }
    // Primitive deps only — selectedBike / genericProduct object identity can
    // change without meaningful value changes (e.g. landingProduct re-spread).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    matchedListing?.id,
    isActive,
    hasVehicle,
    isLoading,
    vehicleKey,
    genericProductId,
    genericProductTitle,
  ]);

  const value = useMemo<WarehouseContextValue>(
    () => ({
      isActive,
      isLoading,
      mode,
      noFit,
      useGroupTitle,
      setUseGroupTitle,
      relatedItems,
      fittingItems,
      matchedListing,
      aggregatedFitment,
    }),
    [
      isActive,
      isLoading,
      mode,
      noFit,
      useGroupTitle,
      setUseGroupTitle,
      relatedItems,
      fittingItems,
      matchedListing,
      aggregatedFitment,
    ]
  );

  return (
    <WarehouseContext.Provider value={value}>
      {children}
    </WarehouseContext.Provider>
  );
}

export function useWarehouse(): WarehouseContextValue {
  return useContext(WarehouseContext) ?? INACTIVE;
}
